import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface StatusBadgeOption {
  label: string;
  icon: ReactNode;
  bgClassName: string;
  borderClassName: string;
  textClassName: string;
}

export type StatusBadgeConfigMap<T extends string> = Record<T, StatusBadgeOption>;

// The tighter geometry used wherever a badge sits inline with a line of text
// rather than alone in its own column.
export const STATUS_BADGE_COMPACT = 'gap-1 py-1 pl-2 pr-2.5';

export interface StatusBadgeProps<T extends string> {
  value: T;
  config: StatusBadgeConfigMap<T>;
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function StatusBadge<T extends string>({
  value,
  config,
  className,
}: StatusBadgeProps<T>) {
  const option = config[value];

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-[5px] border px-3 py-1.5',
        option.bgClassName,
        option.borderClassName,
        className,
      )}
    >
      {option.icon}
      <span className={cn('font-inter text-xs leading-[1.25] font-semibold', option.textClassName)}>
        {option.label}
      </span>
    </div>
  );
}
